import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiMapPin, FiX } = FiIcons;

const AddressInput = ({ onLocationFound, onLoading, onError, onReset }) => {
  const [address, setAddress] = useState('');
  const [hasResults, setHasResults] = useState(false);

  // Calculate antipode coordinates
  const calculateAntipode = (lat, lng) => {
    const antipodeLat = -lat;
    let antipodeLng = lng >= 0 ? lng - 180 : lng + 180;
    return { lat: antipodeLat, lng: antipodeLng };
  };

  // Geocode address using Nominatim (OpenStreetMap)
  const geocodeAddress = async (searchAddress) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchAddress)}&limit=1&addressdetails=1`
      );
      
      if (!response.ok) {
        throw new Error('Geocoding service unavailable');
      }
      
      const data = await response.json();
      
      if (data.length === 0) {
        throw new Error('Address not found. Please try a different address.');
      }

      const result = data[0];
      return {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        address: result.display_name
      };
    } catch (error) {
      throw new Error(error.message || 'Failed to find address. Please try again.');
    }
  };

  // Reverse geocode coordinates
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      
      if (!response.ok) {
        return { address: `Ocean or remote area (${lat.toFixed(4)}°, ${lng.toFixed(4)}°)` };
      }
      
      const data = await response.json();
      return {
        address: data.display_name || `${lat.toFixed(4)}°, ${lng.toFixed(4)}°`
      };
    } catch (error) {
      return { address: `Ocean or remote area (${lat.toFixed(4)}°, ${lng.toFixed(4)}°)` };
    }
  };

  const handleSearch = async () => {
    if (!address.trim()) {
      onError('Please enter an address');
      return;
    }

    onLoading(true);
    onError(null);

    try {
      // Geocode the input address
      const original = await geocodeAddress(address.trim());
      
      // Calculate antipode
      const antipodeCoords = calculateAntipode(original.lat, original.lng);
      
      // Reverse geocode the antipode
      const antipodeInfo = await reverseGeocode(antipodeCoords.lat, antipodeCoords.lng);
      
      const originalLocation = {
        ...original,
        coordinates: `${original.lat.toFixed(4)}°, ${original.lng.toFixed(4)}°`
      };
      
      const antipodeLocation = {
        lat: antipodeCoords.lat,
        lng: antipodeCoords.lng,
        address: antipodeInfo.address,
        coordinates: `${antipodeCoords.lat.toFixed(4)}°, ${antipodeCoords.lng.toFixed(4)}°`
      };

      onLocationFound(originalLocation, antipodeLocation);
      setHasResults(true);
    } catch (error) {
      onError(error.message);
    } finally {
      onLoading(false);
    }
  };

  const handleReset = () => {
    setAddress('');
    setHasResults(false);
    onReset();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <motion.div
      className="max-w-2xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="relative">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <SafeIcon
              icon={FiMapPin}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-xl"
            />
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter any address (e.g., 1600 Pennsylvania Ave, Washington DC)"
              className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-slate-400 text-lg focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-300"
            />
          </div>
          
          <motion.button
            onClick={handleSearch}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold flex items-center gap-2 hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <SafeIcon icon={FiSearch} className="text-xl" />
            <span className="hidden sm:inline">Dig!</span>
          </motion.button>
          
          {hasResults && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={handleReset}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-4 bg-slate-700/50 text-slate-300 rounded-xl hover:bg-slate-600/50 transition-all duration-300"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </motion.button>
          )}
        </div>
      </div>

      {/* Example addresses */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-6 text-center"
      >
        <p className="text-slate-400 text-sm mb-3">Try these examples:</p>
        <div className="flex flex-wrap justify-center gap-2">
          {[
            'Times Square, New York',
            'Eiffel Tower, Paris',
            'Sydney Opera House',
            'Mount Fuji, Japan'
          ].map((example) => (
            <button
              key={example}
              onClick={() => setAddress(example)}
              className="px-3 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-slate-300 text-sm transition-all duration-200"
            >
              {example}
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AddressInput;